'use client';

import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import { useEffect, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper/types";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import React from "react";
import Headline from "@/components/ui/Headline/Headline";
import ServiceBenefitCard from "@/components/client/ServiceBenefitCard/ServiceBenefitCard";
import { LanguageType, LanguageDirection, SubserviceSectionContent, Partners} from "@/lib/types";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(ScrollTrigger);

interface OfferedServicesSectionProps {
  language?: LanguageType
  headlineText: string
  subheadingText: string
  paragraphText: string
  subservices: SubserviceSectionContent[]
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

interface ImageCard {
  imageUrl: string;
  altText: string;
  title: string;
  description: string;
  poweredBy?: Partners;
}

// const imageCards: ImageCard[] = [
//   {
//     id: 1,
//     imageUrl: "https://picsum.photos/693/808?random=1",
//     title: "Card 1",
//     description: "Description for card 1",
//   },
//   {
//     id: 2,
//     imageUrl: "https://picsum.photos/693/808?random=2",
//     title: "Card 2",
//     description: "Description for card 2",
//   },
//   {
//     id: 3,
//     imageUrl: "https://picsum.photos/693/808?random=3",
//     title: "Card 3",
//     description: "Description for card 3",
//   },
// ];


const OfferedServicesSection: React.FC<OfferedServicesSectionProps> = ({
  language = "english",
  headlineText,
  subheadingText,
  paragraphText,
  subservices
}) => {
  const isLg = useIsLg();
  const swiperRef = useRef<SwiperType>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // Card dimensions are defined via CSS container-query custom properties
  // on `.offered-services-container` (see globals.css). 100cqw resolves to
  // the container's inline size at CSS time — before any JS runs — so cards
  // render at the correct size on the very first paint. No JS measurement,
  // no isContainerReady gate, no post-mount layout shift, no
  // ScrollTrigger.refresh() storm.
  const cardNormalWidth = 'var(--card-normal-w)';
  const cardExpandedWidth = 'var(--card-expanded-w)';
  const cardHeight = 'var(--card-h)';

  const imageCards: ImageCard[] = subservices.map((subservice) => {
    // Convert deviceName to lowercase and check if it matches a Partners enum value
    const deviceName = subservice.beautyDevice?.deviceName?.toLowerCase();
    const poweredBy = (deviceName === 'hitek' || deviceName === 'teslamed')
      ? deviceName as Partners
      : undefined;

    return {
      imageUrl: subservice.subserviceImage.url,
      altText: subservice.subserviceImageAltText,
      title: subservice.subserviceTitle,
      description: subservice.subserviceDescription,
      poweredBy
    };
  });

  // No calculateCardWidths, no isContainerReady, no resize handler, no
  // ScrollTrigger.refresh(). Card sizes are pure CSS now (container queries).



  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(swiperRef.current.activeIndex + 1, 300, true);
    }
  };

  const handlePrev = () => {
    if (swiperRef.current) {
      const prevIndex = swiperRef.current.activeIndex - 1;
      swiperRef.current.slideTo(prevIndex, 300, true);
    }
  };

  const handleCardClick = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index, 300);
    }
  };

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];
    }
  }, []);

  // Centralized resize observer for service cards
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (swiperRef.current) {
        swiperRef.current.update();
      }
    });

    // Observe all service cards
    const serviceCards = document.querySelectorAll('.service-card');
    serviceCards.forEach((card) => {
      resizeObserver.observe(card);
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // =============================================================================
  // PARALLAX EFFECT - Completely rewritten for stability with dynamic sizing
  // =============================================================================
  //
  // Key principles:
  // 1. Scope card selection to THIS container only (not global)
  // 2. Use unique ScrollTrigger ID per container instance
  // 3. Use invalidateOnRefresh for accurate positions after resize
  // 4. Properly kill with revert (true) to reset transforms
  // 5. Minimal dependencies - only react to screen size changes
  // =============================================================================

  // Generate a unique ID for this component instance's ScrollTrigger
  const scrollTriggerIdRef = useRef(`parallax-${Math.random().toString(36).substr(2, 9)}`);

  // Parallax animation effect
  useGSAP(() => {
    const container = containerRef.current;
    if (!container) return;

    // IMPORTANT: Scope card selection to THIS container only
    const cards: HTMLElement[] = Array.from(
      container.querySelectorAll('.service-card-image')
    );

    // Helper to reset transforms on cards
    const resetCardTransforms = () => {
      cards.forEach(card => {
        gsap.killTweensOf(card);
        // Set y transforms to 0, but don't clear all props (preserve backgroundImage, scale, etc)
        gsap.set(card, { y: 0, yPercent: 0 });
        card.style.willChange = 'auto';
      });
    };

    // If not on large screen or no cards found, reset and exit
    if (!isLg || cards.length === 0) {
      // Kill any existing ScrollTrigger for this instance
      const existingTrigger = ScrollTrigger.getById(scrollTriggerIdRef.current);
      if (existingTrigger) {
        existingTrigger.kill(true); // true = revert to original values
      }
      resetCardTransforms();
      return;
    }

    // Create the parallax animation.
    //
    // Note: we deliberately DO NOT set `invalidateOnRefresh: true` here even
    // though it's tempting. The tween's from/to are static constants
    // (yPercent -10 → 10), so there's nothing to re-record on refresh.
    // Setting it would cause GSAP to re-read the current yPercent as the new
    // "from" value on every refresh — and if refresh happens mid-scrub, the
    // new "from" is the interpolated value, shrinking the parallax range.
    // That was the root cause of the previous "parallax breaks after
    // refresh" symptom that the (removed) scroll-nudge hack compensated for.
    const tween = gsap.fromTo(cards,
      { yPercent: -10 },
      {
        yPercent: 10,
        ease: "none",
        force3D: true,
        scrollTrigger: {
          id: scrollTriggerIdRef.current,
          trigger: container,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          // markers: true,
          onToggle: (self) => {
            // Optimize: only use willChange when animation is active
            cards.forEach(card => {
              card.style.willChange = self.isActive ? "transform" : "auto";
            });
          }
        }
      }
    );

    // Cleanup function
    return () => {
      // Kill the ScrollTrigger with revert (true = reset to pre-animation state)
      if (tween.scrollTrigger) {
        tween.scrollTrigger.kill(true);
      }
      tween.kill();
      resetCardTransforms();
    };
  }, {
    dependencies: [isLg],
    scope: containerRef, // Scope GSAP queries to this container
    revertOnUpdate: true
  });


  return (
    // section has custom padding!
    <section
      style={{ overscrollBehaviorX: 'none' }}
      className="
      w-full flex flex-col items-center justify-center
      gap-[2rem] lg:gap-[6rem]
      padding-main-x-mobile
      overflow-hidden
      padding-main-x-desktop
      ">
      <div className="w-full container-large">
        <div
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
          className="
            w-full flex flex-col lg:flex-row lg:flex-wrap
            gap-y-[2rem] lg:gap-y-[3.5rem] lg:gap-x-[2rem]
            px-[0.5rem] lg:px-0
        ">
          <Headline
            type="short"
            alignment={language === "english" ? 'left' : 'right'}
            heading={headlineText}
            paragraph={paragraphText}
            subheading={subheadingText}
            language={language}
            useH3={true}
          />

          {isLg && <div style={{ direction: "ltr" }} className="flex-grow min-w-[11.6875rem] w-[11.6875rem] max-w-full self-end">
            <div className={`flex flex-grow flex-row ${language === "english" ? 'justify-end' : 'justify-start'} gap-[0.75rem] lg:gap-[1rem]`}>
              <CompactButton
                size={isLg ? 64 : 44}
                radius={9999}
                className="left-0 bottom-0"
                onClick={language === "english" ? handlePrev : handleNext}
                disabled={language === "english" ? isBeginning : isEnd}
              >
                <CaretLeft />
              </CompactButton>

              <CompactButton
                size={isLg ? 64 : 44}
                radius={9999}
                className="right-0 bottom-0"
                onClick={language === "english" ? handleNext : handlePrev}
                disabled={language === "english" ? isEnd : isBeginning}
              >
                <CaretRight />
              </CompactButton>
            </div>
          </div>}
        </div>
      </div>

      <div
        ref={containerRef}
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className="w-full container-large overflow-visible offered-services-container">
        {/* Hidden SEO content - always present in DOM for crawlers */}
        <div className="sr-only-seo">
          {imageCards.map((card) => (
            <article key={`seo-${card.title}`}>
              <h4>{card.title}</h4>
              <p>{card.description}</p>
            </article>
          ))}
        </div>

        <Swiper
            modules={[Mousewheel]}
            spaceBetween={isLg ? 16 : 12}
            style={{ overflow: 'visible' }}
            slidesPerView="auto"
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
            onAfterInit={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            updateOnWindowResize={false}
            observer={true}
            observeParents={true}
            resizeObserver={false}
            centeredSlidesBounds={false}
            centeredSlides={false}
            // mousewheel={{
            //   forceToAxis: true,
            //   sensitivity: 0.5,
            //   releaseOnEdges: true,
            // }}
          >
            {imageCards.map((card, index) => (
              <SwiperSlide
                key={card.title}
                style={{ width: 'auto' }}
                className="cursor-grab"
              >
                <div className="service-card">
                  <ServiceBenefitCard
                     imageSrc={card.imageUrl}
                     imageAlt={card.altText}
                     poweredBy={card.poweredBy}
                     title={card.title}
                     description={card.description}
                     buttonText={ language === "arabic" ? "انضم لقائمة الانتظار" : "Join our waitlist" }
                     onButtonClick={() => handleCardClick(index)}
                     normalWidth={cardNormalWidth}
                     expandedWidth={cardExpandedWidth}
                     height={cardHeight}
                     language={language}
                   />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
      </div>
    </section>
  );
}

export default OfferedServicesSection;
